import React, { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, List, X } from 'lucide-react';
import { parseEmailsFromText } from './parseRecipientFile';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface EmailRecipientInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  suggestions?: string[];
}

function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}

function mergeEmails(existing: string[], incoming: string[]): string[] {
  const seen = new Set(existing.map((e) => e.toLowerCase()));
  const merged = [...existing];
  for (const raw of incoming) {
    const email = raw.trim().toLowerCase();
    if (email && isValidEmail(email) && !seen.has(email)) {
      seen.add(email);
      merged.push(email);
    }
  }
  return merged;
}

const EmailRecipientInput: React.FC<EmailRecipientInputProps> = ({
  value,
  onChange,
  placeholder = 'Recipients',
  disabled = false,
  suggestions = [],
}) => {
  const [input, setInput] = useState('');
  const [invalidHint, setInvalidHint] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showBrowse, setShowBrowse] = useState(false);
  const [browseSearch, setBrowseSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedSet = useMemo(() => new Set(value.map((e) => e.toLowerCase())), [value]);

  const filteredSuggestions = useMemo(() => {
    const q = input.trim().toLowerCase();
    return suggestions
      .filter((email) => !selectedSet.has(email.toLowerCase()))
      .filter((email) => !q || email.toLowerCase().includes(q))
      .slice(0, 8);
  }, [input, suggestions, selectedSet]);

  const browseList = useMemo(() => {
    const q = browseSearch.trim().toLowerCase();
    return suggestions
      .filter((email) => !selectedSet.has(email.toLowerCase()))
      .filter((email) => !q || email.toLowerCase().includes(q))
      .slice(0, 100);
  }, [browseSearch, suggestions, selectedSet]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
        setShowBrowse(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addEmail = useCallback(
    (raw: string) => {
      const email = raw.trim().replace(/,$/, '').toLowerCase();
      if (!email) return false;
      if (!isValidEmail(email)) {
        setInvalidHint('Enter a valid email address');
        return false;
      }
      if (value.some((e) => e.toLowerCase() === email)) {
        setInput('');
        return true;
      }
      onChange([...value, email]);
      setInput('');
      setInvalidHint('');
      setShowSuggestions(false);
      return true;
    },
    [onChange, value]
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab' || e.key === ' ') {
      if (input.trim()) {
        e.preventDefault();
        addEmail(input);
      }
    } else if (e.key === 'Backspace' && !input && value.length > 0) {
      onChange(value.slice(0, -1));
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setShowBrowse(false);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (!text || (!text.includes(',') && !text.includes('\n') && !text.includes(';') && !text.includes('\t'))) {
      return;
    }
    e.preventDefault();
    const parsed = parseEmailsFromText(text);
    if (parsed.length) {
      onChange(mergeEmails(value, parsed));
      setInput('');
      setInvalidHint('');
    }
  };

  const removeEmail = (email: string) => {
    onChange(value.filter((e) => e !== email));
  };

  return (
    <div className="min-w-0 flex-1" ref={containerRef}>
      <div
        className={[
          'flex min-h-[2rem] flex-wrap items-center gap-1.5',
          value.length > 50
            ? 'max-h-52 overflow-y-auto overscroll-contain pr-1'
            : value.length > 8
              ? 'max-h-36 overflow-y-auto overscroll-contain pr-1'
              : '',
        ].join(' ')}
      >
        {value.map((email) => (
          <span
            key={email}
            className="inline-flex max-w-full items-center gap-1 rounded-full border border-blue-200/80 bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:border-blue-800/60 dark:bg-blue-950/50 dark:text-blue-200"
          >
            <span className="truncate">{email}</span>
            {!disabled && (
              <button
                type="button"
                onClick={() => removeEmail(email)}
                className="rounded-full p-0.5 hover:bg-blue-100 dark:hover:bg-blue-900/60"
                aria-label={`Remove ${email}`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <div className="relative min-w-[10rem] flex-1">
            <input
              type="text"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setInvalidHint('');
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onBlur={() => {
                if (input) addEmail(input);
              }}
              placeholder={value.length === 0 ? placeholder : 'Add another…'}
              className="w-full border-0 bg-transparent py-1 text-sm outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500"
            />

            {showSuggestions && filteredSuggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900">
                {filteredSuggestions.map((email) => (
                  <button
                    key={email}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => addEmail(email)}
                    className="block w-full truncate px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
                  >
                    {email}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {!disabled && suggestions.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setShowBrowse((v) => !v);
              setShowSuggestions(false);
            }}
            className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-primary dark:text-gray-400 dark:hover:bg-gray-800"
            title="Browse saved emails"
          >
            <List className="h-3.5 w-3.5" />
            Browse
            <ChevronDown className={`h-3 w-3 transition ${showBrowse ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {showBrowse && suggestions.length > 0 && (
        <div className="mt-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <input
            type="search"
            value={browseSearch}
            onChange={(e) => setBrowseSearch(e.target.value)}
            placeholder="Search saved emails…"
            className="mb-2 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm outline-none focus:border-primary dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
          />
          <div className="max-h-40 overflow-y-auto">
            {browseList.length === 0 ? (
              <p className="px-2 py-3 text-center text-xs text-gray-400">No matching emails</p>
            ) : (
              browseList.map((email) => (
                <button
                  key={email}
                  type="button"
                  onClick={() => addEmail(email)}
                  className="block w-full truncate rounded-lg px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  {email}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {invalidHint && <p className="mt-0.5 text-xs text-red-500">{invalidHint}</p>}
    </div>
  );
};

export { mergeEmails };
export default EmailRecipientInput;
